import { FC, useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Alert, Typography, Result } from 'antd'
import { LockOutlined, CloseOutlined, CheckCircleTwoTone } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { PasswordStrengthIndicator } from '../ui/PasswordStrengthIndicator'
import './auth-modal.css'

const { Title, Text } = Typography

interface ResetPasswordModalProps {
  visible: boolean
  onClose: () => void
  hasResetToken?: boolean
}

export const ResetPasswordModal: FC<ResetPasswordModalProps> = ({
  visible,
  onClose,
  hasResetToken = false
}) => {
  const { t } = useTranslation(['common', 'auth'])
  const { resetPasswordWithToken, resetPasswordState, clearResetPasswordState } = useAuth()
  const [newPassword, setNewPassword] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // 只有在需要显示表单时才创建form实例
  const shouldShowForm = hasResetToken || !urlError
  const [form] = Form.useForm()

  // 检查URL中的错误信息
  useEffect(() => {
    if (!hasResetToken) {
      // 尝试从URL获取错误信息
      const urlParams = new URLSearchParams(window.location.search)
      const error = urlParams.get('error')
      const errorDescription = urlParams.get('error_description')

      if (error || errorDescription) {
        setUrlError(errorDescription || error || '重置链接无效')
      }
    } else {
      setUrlError(null)
    }
  }, [hasResetToken, visible])

  const handleSubmit = async (values: {
    newPassword: string
    confirmPassword: string
  }) => {
    const { success } = await resetPasswordWithToken(values.newPassword)
    if (success) {
      form.resetFields()
    }
  }

  const handleCancel = () => {
    clearResetPasswordState()
    form.resetFields()
    onClose()
  }

  const handleClose = () => {
    if (resetPasswordState.success) {
      clearResetPasswordState()
      form.resetFields()
    }
    onClose()
  }

  // 成功后自动关闭
  useEffect(() => {
    if (resetPasswordState.success) {
      const timer = setTimeout(() => {
        handleClose()
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [resetPasswordState.success])

  // 如果没有明确令牌，但显示弹窗，说明是重置链接访问，需要检测当前认证状态
  useEffect(() => {
    if (visible && !hasResetToken && !urlError) {
      setIsProcessing(true)
      // 给一些时间让Supabase处理认证状态
      const timer = setTimeout(() => {
        setIsProcessing(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [visible, hasResetToken, urlError])

  if (!shouldShowForm) {
    return (
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={400}
        centered
        closable
      >
        <Result
          status="error"
          title={t('auth:resetPassword.invalidToken')}
          subTitle={urlError || t('auth:resetPassword.invalidTokenDescription')}
          extra={[
            <Button key="close" onClick={onClose}>
              {t('common:close')}
            </Button>,
            <Button key="forgot-password" type="primary" onClick={() => {
              onClose()
              // 可以在这里触发忘记密码流程
              window.location.href = '/?forgot-password=true'
            }}>
              重新申请重置
            </Button>
          ]}
        />
      </Modal>
    )
  }

  return (
    <Modal
      open={visible}
      onCancel={resetPasswordState.success ? handleClose : handleCancel}
      footer={null}
      width={resetPasswordState.success ? 400 : 450}
      centered
      closable={!resetPasswordState.success}
      maskClosable={!resetPasswordState.success}
      closeIcon={!resetPasswordState.success ? <CloseOutlined className="auth-modal__close-icon" /> : null}
    >
      {/* 密码重置成功状态 */}
      {resetPasswordState.success ? (
        <Result
          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          status="success"
          title={t('auth:resetPassword.success')}
          subTitle={t('auth:resetPassword.successDescription')}
        />
      ) : isProcessing ? (
        <div className="auth-modal__processing">
          <div className="auth-modal__processing-title">
            正在验证重置链接...
          </div>
          <div className="auth-modal__processing-subtitle">
            请稍候，我们正在处理您的重置请求
          </div>
        </div>
      ) : (
        <>
          <div className="auth-modal__header">
            <Title level={2} className="auth-modal__title">
              {t('auth:resetPassword.title')}
            </Title>
            <Text type="secondary" className="auth-modal__subtitle">
              {t('auth:resetPassword.subtitle')}
            </Text>
            {!hasResetToken && (
              <div className="auth-modal__warning">
                ⚠️ 请确保您是通过邮件中的重置链接访问此页面
              </div>
            )}
          </div>

          {resetPasswordState.error && (
            <Alert
              message={resetPasswordState.error}
              type="error"
              showIcon
              className="auth-modal__alert"
              closable
              onClose={clearResetPasswordState}
            />
          )}

          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="newPassword"
              label={t('auth:resetPassword.newPassword')}
              rules={[
                { required: true, message: t('auth:validation.newPasswordRequired') },
                { min: 8, message: t('auth:validation.passwordMinLength') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="auth-modal__input-icon" />}
                placeholder={t('auth:resetPassword.placeholder.newPassword')}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Item>

            {newPassword && <PasswordStrengthIndicator password={newPassword} />}

            <Form.Item
              name="confirmPassword"
              label={t('auth:resetPassword.confirmPassword')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('auth:validation.confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t('auth:validation.passwordMismatch')))
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="auth-modal__input-icon" />}
                placeholder={t('auth:resetPassword.placeholder.confirmPassword')}
                autoComplete="new-password"
              />
            </Form.Item>

            
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={resetPasswordState.loading}
                className="auth-modal__submit"
              >
                {t('auth:resetPassword.submit')}
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  )
}
